<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import useSearchStore from '@shop/store/search';
import useMaskStore from '@shop/store/mask';
import Spinner from '@shop/components/ui/Spinner.vue';
import SearchResult from './SearchResult.vue';
import { useFlash } from '@shop/composables/useFlash';
import { generateUrl } from '@shop/utils/Routing';
import { lang, useTranslation } from '@shop/utils/Translation';

const mask = useMaskStore();
const searchStore = useSearchStore();
const flash = useFlash();

const { t } = useTranslation('searchinput');

const QUERY_MIN_LENGTH = 2;
const isInformalStore = document.documentElement.dataset.storeInformal === 'true';
const placeholderKey = isInformalStore ? 'search.looking_for_informal' : 'search.looking_for';
const searchQuery = ref('');
const isSearchActive = ref(false);
const isInputFocused = ref(false);
const selectedIndex = ref(-1);

const isLoading = computed(() => searchStore.isLoading);
const history = computed(() => searchStore.historyItems);
const suggestions = computed(() => searchStore.suggestions);
const products = computed(() => searchStore.products);
const hasSearchHistory = computed(() => history.value.length > 0);

const showResults = computed(() => {
  if (!isSearchActive.value) {
    return false;
  }
  const hasSearchQuery = searchQuery.value.trim().length > 0;
  return (
    (hasSearchQuery && (suggestions.value.length > 0 || products.value.length > 0)) ||
    (!hasSearchQuery && history.value.length > 0)
  );
});

async function showRefineQueryFlash(): Promise<void> {
  flash.clear();
  await flash.showTranslatedError('search.refine_query', 'flash_messages');
  mask.close();
  isSearchActive.value = false;
}

async function navigateToSearch(query: string): Promise<void> {
  const trimmed = query.trim();

  if (!trimmed) {
    return;
  }

  if (trimmed.length < QUERY_MIN_LENGTH) {
    await showRefineQueryFlash();
    return;
  }

  searchStore.addToHistory(trimmed);
  window.location.href = generateUrl(`search.${lang}`, { q: trimmed });
}

const handleSearch = async () => {
  await navigateToSearch(searchQuery.value);
};

const handleDoSearch = async (searchTerm: string) => {
  await navigateToSearch(searchTerm);
};

const handleSaveToHistory = (searchTerm: string) => {
  if (!searchTerm.trim()) {
    return;
  }

  searchStore.addToHistory(searchTerm);
};

let debounceTimeout: number | undefined;
let debounceHistoryTimeout: number | undefined;

const triggerAutocomplete = () => {
  const query = searchQuery.value.trim();

  if (!query) {
    searchStore.clearResults();
    return;
  }

  searchStore.search(query);
};

const addToHistoryDebounced = () => {
  const query = searchQuery.value.trim();

  if (query && selectedIndex.value === -1) {
    searchStore.addToHistory(query);
  }
};

const handleInput = () => {
  if (debounceTimeout !== undefined) {
    window.clearTimeout(debounceTimeout);
  }

  if (debounceHistoryTimeout !== undefined) {
    window.clearTimeout(debounceHistoryTimeout);
  }

  debounceTimeout = window.setTimeout(triggerAutocomplete, 500);
  debounceHistoryTimeout = window.setTimeout(addToHistoryDebounced, 500);
};

const clearSearch = () => {
  searchQuery.value = '';
  searchStore.clearResults();
};

const clearHistory = () => {
  searchStore.clearHistory();
};

const handleMouseEnter = () => {
  mask.show();
  isSearchActive.value = true;
};

const handleMouseLeave = () => {
  if (!isInputFocused.value) {
    mask.close();
    isSearchActive.value = false;
  }
};

const handleFocus = () => {
  mask.show();
  isInputFocused.value = true;
  isSearchActive.value = true;
};

const searchContainerRef = ref<HTMLElement | null>(null);

const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement | null;

  if (relatedTarget && searchContainerRef.value?.contains(relatedTarget)) {
    return;
  }

  if (searchContainerRef.value && !searchContainerRef.value.contains(document.activeElement)) {
    mask.close();
    isInputFocused.value = false;
    isSearchActive.value = false;
  }
};

const handleFocusOut = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement | null;

  if (!relatedTarget || !searchContainerRef.value?.contains(relatedTarget)) {
    mask.close();
    isInputFocused.value = false;
    isSearchActive.value = false;
  }
};

const getHistoryIndex = (index: number) => index;

const getSuggestIndex = (index: number) => {
  if (hasSearchHistory.value && !searchQuery.value.trim()) {
    return history.value.length + index;
  }

  return index;
};

const getProductIndex = (index: number) => {
  const hasQuery = searchQuery.value.trim().length > 0;
  const historyOffset = !hasQuery && hasSearchHistory.value ? history.value.length : 0;

  return historyOffset + suggestions.value.length + index;
};

const handleKeyDown = (event: KeyboardEvent) => {
  const hasQuery = searchQuery.value.trim().length > 0;

  if (!showResults.value) {
    return;
  }

  let totalItems = 0;

  if (!hasQuery && hasSearchHistory.value) {
    totalItems += history.value.length;
  }

  if (suggestions.value.length > 0) {
    totalItems += suggestions.value.length;
  }

  if (products.value.length > 0) {
    totalItems += products.value.length;
  }

  if (totalItems === 0) {
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();

    if (selectedIndex.value < totalItems - 1) {
      selectedIndex.value += 1;
    } else {
      selectedIndex.value = -1;
    }
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();

    if (selectedIndex.value === -1) {
      selectedIndex.value = totalItems - 1;
    } else if (selectedIndex.value > -1) {
      selectedIndex.value -= 1;
    }
  }
};

const highlightTerm = (term: string): string => {
  if (!searchQuery.value.trim()) return term;

  let highlightedTerm = term;
  const parts = searchQuery.value.split(' ');

  parts.forEach(part => {
    if (part.length > 3) {
      const regex = new RegExp('(' + part + ')', 'gi');
      highlightedTerm = highlightedTerm.replace(regex, '<span class="regular">$1</span>');
    }
  });

  return highlightedTerm;
};

onMounted(() => {
  if (!searchQuery.value && typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');

    if (q) {
      searchQuery.value = q.replace(/{/g, '').replace(/}/g, '');
    }
  }
});

onUnmounted(() => {
  if (debounceTimeout !== undefined) {
    window.clearTimeout(debounceTimeout);
  }
  if (debounceHistoryTimeout !== undefined) {
    window.clearTimeout(debounceHistoryTimeout);
  }
});
</script>

<template>
  <div
    ref="searchContainerRef"
    class="search__container"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focusout="handleFocusOut"
  >
    <input
      v-model="searchQuery"
      class="search__input"
      type="text"
      :placeholder="t(placeholderKey)"
      @input="handleInput"
      @keydown="handleKeyDown"
      @focus="handleFocus"
      @blur="handleBlur"
      @keyup.enter="handleSearch"
    />
    <button :disabled="isLoading" class="search__button" @click="handleSearch">
      <i class="icon icon--search icon--showleft"></i>
    </button>
    <button v-if="searchQuery" class="search__cancel" @click="clearSearch">
      <i class="search__cancel-icon icon icon--cancel icon--showleft"></i>
    </button>

    <div v-if="showResults" class="search__result-container">
      <div v-if="isLoading" class="search__spinner"><Spinner v-cloak /></div>

      <div v-else>
        <template v-if="hasSearchHistory && !searchQuery.trim()">
          <span class="search__title-container">
            <strong class="search__title">{{ t('search.history') }}</strong>
          </span>
          <span class="search__delete-history-container">
            <a
              class="search__delete-history"
              tabindex="0"
              @keyup.enter="clearHistory()"
              @click="clearHistory()"
            >
              {{ t('search.delete') }}
            </a>
          </span>
          <ul class="search__terms-list">
            <SearchResult
              v-for="(historyItem, index) in history"
              :key="`history-${index}`"
              :index="getHistoryIndex(index)"
              :search-term="historyItem"
              :selected-index="selectedIndex"
              @do-search="handleDoSearch"
            >
              {{ historyItem }}
            </SearchResult>
          </ul>
        </template>
        <template v-if="suggestions.length > 0">
          <strong class="search__title">{{ t('search.suggestions') }}</strong>
          <ul class="search__terms-list">
            <SearchResult
              v-for="(suggestion, index) in suggestions"
              :key="`suggestion-${index}`"
              :index="getSuggestIndex(index)"
              :search-href="generateUrl(`search.${lang}`, { q: suggestion })"
              :search-term="suggestion"
              :selected-index="selectedIndex"
              class="search__term--bold"
              @save-history="handleSaveToHistory"
            >
              <span v-html="highlightTerm(suggestion)"></span>
            </SearchResult>
          </ul>
        </template>

        <template v-if="products.length > 0">
          <strong class="search__title">{{ t('search.recommended') }}</strong>
          <ul class="search__terms-list">
            <SearchResult
              v-for="(product, index) in products"
              :key="`product-${product.uri}`"
              :index="getProductIndex(index)"
              :search-href="product.uri"
              :selected-index="selectedIndex"
            >
              {{ product.name }}
            </SearchResult>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@shop/assets/scss/abstracts/variables' as *;

.search {
  &__container {
    background: #f5f5f5;
    position: relative;
    top: 0;
    height: 64px;
    margin: 0;
    width: 100%;

    @media (min-width: 768px) {
      background: none;
      position: absolute;
      top: 22px;
      height: 47px;
      width: 250px;
      left: 330px;
    }

    @media (min-width: 1024px) {
      width: 325px;
      left: 430px;
    }

    @media (min-width: 1280px) {
      width: 400px;
      left: 430px;
    }
  }

  &__cancel-icon {
    height: 27px;
  }

  &__input {
    position: absolute;
    border-radius: 5px;
    border: 1px solid #808080;
    height: 40px;
    line-height: 38px;
    text-indent: 12px;
    top: 12px;
    left: 10px;
    margin: 0;
    padding: 0;
    width: calc(100% - 30px);
    -webkit-appearance: none;
    font-size: 16px;

    &:focus {
      box-shadow: none;
      outline: none;
    }

    @media (min-width: 768px) {
      height: 45px;
      text-indent: 20px;
      font-size: 1.2rem;
      left: 0;
      top: 0;
      width: 247px;
    }

    @media (min-width: 1024px) {
      width: 322px;
      font-size: 1.2rem;
    }

    @media (min-width: 1280px) {
      width: 397px;
    }
  }

  &__button {
    background: #808080;
    position: absolute;
    border-radius: 0 5px 5px 0;
    border: 1px solid #808080;
    border-left: 0;
    color: white;
    height: 40px;
    width: 40px;
    line-height: 20px;
    top: 12px;
    right: 10px;
    cursor: pointer;

    .icon {
      height: 24px;
    }

    @media (min-width: 768px) {
      background: #f5f5f5;
      color: #6a757c;
      top: 0;
      right: 1px;
      height: 45px;
      width: 45px;
      font-size: 1.5em;
    }
  }

  &__cancel {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    font-size: 2em;
    padding: 0 10px 0 0;
    line-height: 40px;
    height: 40px;
    width: 30px;
    top: 12px;
    right: 50px;
    cursor: pointer;
    outline: 0;
    color: $subgray;

    @media (min-width: 768px) {
      padding-right: 15px;
      line-height: 45px;
      height: 45px;
      width: 32px;
      top: 0;
      right: 47px;
    }
  }

  &__terms-list {
    list-style-type: none;
    padding: 0;
    margin: 0;
    clear: both;
  }

  &__term {
    &--bold {
      font-weight: bold;
    }

    &:last-child {
      border-bottom: 1px solid #e0e0e0;
    }

    .regular {
      font-weight: normal;
    }
  }

  &__title-container,
  &__delete-history-container {
    display: inline-block;
    width: 50%;
  }

  &__delete-history-container {
    text-align: right;
  }

  &__delete-history {
    display: inline-block;
    text-decoration: underline;
    cursor: pointer;
    padding: 5px 10px;
  }

  &__title {
    display: inline-block;
    padding: 5px 10px;
  }

  &__result-container {
    background: white;
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 2;
    padding-top: 10px;
    width: 100%;
    height: auto;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &__spinner {
    text-align: center;
    margin: 5px 0;
  }
}

.force-zindex--high {
  z-index: 1000;
}

.force-zindex--low {
  z-index: 999;
}
</style>
